/*
 * loot-ww theme.js — motion core.
 * Modules register only when their [data-*] hook exists on the page.
 * Reduced-motion behavior is defined per module (see SPEC.md §6).
 */
(function () {
  'use strict';

  var REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var DESKTOP = window.matchMedia('(min-width: 768px)');
  var TOUCH = window.matchMedia('(hover: none)').matches;

  var Loot = (window.Loot = window.Loot || {});

  /* ── Loot.scroll — Lenis smooth scroll synced to ScrollTrigger ── */
  Loot.scroll = (function () {
    var lenis = null;

    function init() {
      if (REDUCED || TOUCH || !window.Lenis) return;

      /* 0.15 — snappier catch-up than the floaty 0.1 default (owner S19:
         site felt sluggish) */
      lenis = new Lenis({ lerp: 0.15 });

      if (window.gsap && window.ScrollTrigger) {
        lenis.on('scroll', ScrollTrigger.update);
        gsap.ticker.add(function (time) {
          lenis.raf(time * 1000);
        });
        gsap.ticker.lagSmoothing(0);
      } else {
        function raf(time) {
          lenis.raf(time);
          requestAnimationFrame(raf);
        }
        requestAnimationFrame(raf);
      }

      // route same-page anchors through Lenis
      document.addEventListener('click', function (e) {
        var link = e.target.closest('a[href^="#"]');
        if (!link || link.getAttribute('href') === '#') return;
        var target = document.querySelector(link.getAttribute('href'));
        if (!target) return;
        e.preventDefault();
        lenis.scrollTo(target);
      });
    }

    return { init: init, get instance() { return lenis; } };
  })();

  /* ── Loot.loader — full-viewport overlay + 0→100 counter, once per session ── */
  Loot.loader = (function () {
    function init() {
      var el = document.querySelector('[data-loader]');
      if (!el) return;

      if (sessionStorage.getItem('lootLoaded')) {
        el.remove();
        document.body.classList.remove('is-loading');
        return;
      }

      var counter = el.querySelector('[data-loader-counter]');
      var done = false;
      var progress = { value: 0 };
      /* short enough not to feel like a gate — first visit only */
      var MIN_MS = 650;
      var CAP_MS = 2000;
      var t0 = performance.now();

      function render() {
        if (counter) {
          counter.textContent = String(Math.round(progress.value)).padStart(3, '0');
        }
      }

      function finish() {
        if (done) return;
        done = true;
        sessionStorage.setItem('lootLoaded', '1');
        progress.value = 100;
        render();

        function release() {
          el.remove();
          document.body.classList.remove('is-loading');
          document.dispatchEvent(new CustomEvent('loot:loaded'));
        }

        if (REDUCED || !window.gsap) {
          el.style.transition = 'opacity 200ms ease';
          el.style.opacity = '0';
          setTimeout(release, 220);
        } else {
          gsap.to(el, {
            yPercent: -100,
            duration: 0.6,
            ease: 'power3.inOut',
            onComplete: release,
          });
        }
      }

      if (REDUCED) {
        // no counter animation — brief brand beat, then fade
        setTimeout(finish, 300);
      } else {
        var loaded = document.readyState === 'complete';
        window.addEventListener('load', function () { loaded = true; });

        (function tick(now) {
          var elapsed = now - t0;
          // ease toward 100 against the minimum window; stall at 92 until load
          var target = Math.min(elapsed / MIN_MS, 1) * (loaded ? 100 : 92);
          progress.value += (target - progress.value) * 0.12;
          render();
          if ((loaded && elapsed >= MIN_MS && progress.value > 99) || elapsed >= CAP_MS) {
            finish();
          } else {
            requestAnimationFrame(tick);
          }
        })(t0);
      }
    }

    return { init: init };
  })();

  /* ── Loot.reveal — [data-reveal] typographic masks + [data-fade] IO fades ── */
  Loot.reveal = (function () {
    // wrap words (and optionally chars) in overflow-hidden masks
    function split(el, mode) {
      var text = el.textContent;
      el.textContent = '';
      el.setAttribute('aria-label', text);
      var frag = document.createDocumentFragment();
      text.split(/(\s+)/).forEach(function (part) {
        if (!part.trim()) {
          frag.appendChild(document.createTextNode(part));
          return;
        }
        var mask = document.createElement('span');
        mask.className = 'reveal-mask';
        mask.setAttribute('aria-hidden', 'true');
        if (mode === 'chars') {
          part.split('').forEach(function (ch) {
            var c = document.createElement('span');
            c.className = 'reveal-unit';
            c.textContent = ch;
            mask.appendChild(c);
          });
        } else {
          var w = document.createElement('span');
          w.className = 'reveal-unit';
          w.textContent = part;
          mask.appendChild(w);
        }
        frag.appendChild(mask);
      });
      el.appendChild(frag);
      return el.querySelectorAll('.reveal-unit');
    }

    function init() {
      var reveals = document.querySelectorAll('[data-reveal]');
      var fades = document.querySelectorAll('[data-fade]');

      if (REDUCED || !window.gsap || !window.ScrollTrigger) {
        reveals.forEach(function (el) { el.classList.add('is-revealed'); });
        fades.forEach(function (el) { el.classList.add('is-faded-in'); });
        return;
      }

      gsap.registerPlugin(ScrollTrigger);

      reveals.forEach(function (el) {
        var units = split(el, el.getAttribute('data-reveal') || 'words');
        el.classList.add('is-split');
        gsap.from(units, {
          yPercent: 110,
          opacity: 0,
          duration: 0.6,
          ease: 'expo.out',
          stagger: 0.03,
          scrollTrigger: {
            trigger: el,
            // 'top bottom' fires immediately for elements already in view
            // (hero-bottom wordmark); default keeps reveals below the fold
            start: el.getAttribute('data-reveal-start') || 'top 85%',
            once: true,
          },
        });
      });

      // IO-driven fades — cheap, no GSAP per-element cost
      if (fades.length) {
        var io = new IntersectionObserver(
          function (entries) {
            entries.forEach(function (entry) {
              if (!entry.isIntersecting) return;
              entry.target.classList.add('is-faded-in');
              io.unobserve(entry.target);
            });
          },
          { rootMargin: '0px 0px -10% 0px' }
        );
        fades.forEach(function (el, i) {
          el.style.transitionDelay = (i % 6) * 30 + 'ms';
          io.observe(el);
        });
      }
    }

    return { init: init };
  })();

  /* ── Loot.decode — [data-decode] terminal scramble-in for mono text ──
     Characters flicker through a glyph pool and settle left→right when the
     element enters the viewport. Once settled, elements join an ambient
     pool: every few seconds one or two visible lines twitch a couple of
     characters, so the whole interface reads as live/operating.
     Mono type only — sans would reflow. Reduced motion: static, no timers. ── */
  Loot.decode = (function () {
    var GLYPHS = '!<>-_\\/[]{}—=+*^?#01234789ABCDEF';
    var pool = [];

    function scramble(el, dur) {
      var text = el.getAttribute('data-decode-text') || el.textContent;
      el.setAttribute('data-decode-text', text);
      el.setAttribute('aria-label', text);
      el._decoding = true;
      var chars = text.split('');
      var n = chars.length;
      var t0 = performance.now();
      dur = dur || 900;
      (function frame(now) {
        var p = Math.min((now - t0) / dur, 1);
        var settled = Math.floor(p * n);
        var out = '';
        for (var i = 0; i < n; i++) {
          var c = chars[i];
          if (/\s/.test(c) || i < settled) out += c;
          else out += GLYPHS[(Math.random() * GLYPHS.length) | 0];
        }
        el.textContent = out;
        if (p < 1) {
          requestAnimationFrame(frame);
        } else {
          el.textContent = text;
          el._decoding = false;
          if (pool.indexOf(el) === -1) pool.push(el);
        }
      })(t0);
    }

    // one visible line "goes off" — full re-scramble for 0.5–1s, then settles
    function ambient() {
      var visible = pool.filter(function (el) {
        if (!el.isConnected || el._decoding) return false;
        var r = el.getBoundingClientRect();
        return r.top < window.innerHeight && r.bottom > 0;
      });
      if (!visible.length) return;
      var el = visible[(Math.random() * visible.length) | 0];
      scramble(el, 500 + Math.random() * 500);
    }

    function init() {
      var els = document.querySelectorAll('[data-decode]');
      if (!els.length || REDUCED) return;

      var io = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (!entry.isIntersecting) return;
            scramble(entry.target);
            io.unobserve(entry.target);
          });
        },
        { rootMargin: '0px 0px -5% 0px' }
      );
      els.forEach(function (el) { io.observe(el); });

      setInterval(ambient, 4500);
    }

    // re-scramble every decode line inside a container (pane switches)
    function burst(root) {
      if (REDUCED || !root) return;
      root.querySelectorAll('[data-decode]').forEach(function (el) {
        scramble(el, 450);
      });
    }

    return { init: init, burst: burst };
  })();

  /* ── Loot.newsletter — site-wide <dialog> capture; anything with
     [data-newsletter-open] opens it. Re-opens itself after a post
     round-trip (data-nl-autopen) to show the terminal result line. ── */
  Loot.newsletter = (function () {
    function init() {
      var dlg = document.getElementById('newsletter-modal');
      if (!dlg || typeof dlg.showModal !== 'function') return;

      document.addEventListener('click', function (e) {
        var trigger = e.target.closest('[data-newsletter-open]');
        if (!trigger) return;
        e.preventDefault();
        dlg.showModal();
      });

      var close = dlg.querySelector('[data-nl-close]');
      if (close) close.addEventListener('click', function () { dlg.close(); });

      // click on the backdrop (the dialog element itself) closes
      dlg.addEventListener('click', function (e) {
        if (e.target === dlg) dlg.close();
      });

      if (dlg.hasAttribute('data-nl-autopen')) dlg.showModal();
    }

    return { init: init };
  })();

  /* ── Loot.countdown — [data-countdown] DD:HH:MM:SS to an ISO datetime ── */
  Loot.countdown = (function () {
    function init() {
      var els = document.querySelectorAll('[data-countdown]');
      if (!els.length) return;

      els.forEach(function (el) {
        // Prefer epoch seconds (absolute/UTC, timezone-proof) over the raw ISO
        // string, which Date.parse reads in the visitor's local zone (and Safari
        // returns NaN for the space-separated offset form).
        var epoch = parseInt(el.getAttribute('data-countdown-epoch'), 10);
        var target = !isNaN(epoch) && epoch > 0
          ? epoch * 1000
          : Date.parse(el.getAttribute('data-countdown-datetime'));
        if (isNaN(target)) return;
        var segs = {
          d: el.querySelector('[data-countdown-seg="d"]'),
          h: el.querySelector('[data-countdown-seg="h"]'),
          m: el.querySelector('[data-countdown-seg="m"]'),
          s: el.querySelector('[data-countdown-seg="s"]'),
        };
        var time = el.querySelector('.countdown__time');
        var done = el.querySelector('[data-countdown-done]');
        var timer = null;

        function pad(n) { return String(n).padStart(2, '0'); }

        function finish() {
          if (timer) clearInterval(timer);
          if (time) time.hidden = true;
          if (done) done.hidden = false;
          document.dispatchEvent(new CustomEvent('loot:drop-live', { detail: { el: el } }));
        }

        function tick() {
          var diff = target - Date.now();
          if (diff <= 0) return finish();
          var s = Math.floor(diff / 1000);
          if (segs.d) segs.d.textContent = pad(Math.floor(s / 86400));
          if (segs.h) segs.h.textContent = pad(Math.floor((s % 86400) / 3600));
          if (segs.m) segs.m.textContent = pad(Math.floor((s % 3600) / 60));
          if (segs.s) segs.s.textContent = pad(s % 60);
        }

        tick();
        if (target - Date.now() > 0) timer = setInterval(tick, 1000);
      });
    }

    return { init: init };
  })();

  /* ── Loot.clock — [data-clock] live HH:MM:SS, optional data-clock-tz offset label ── */
  Loot.clock = (function () {
    function init() {
      var clocks = document.querySelectorAll('[data-clock]');
      if (!clocks.length) return;

      function tick() {
        clocks.forEach(function (el) {
          var tz = el.getAttribute('data-clock-tz'); // IANA zone, optional
          var now = new Date();
          var opts = { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false };
          if (tz) opts.timeZone = tz;
          el.textContent = new Intl.DateTimeFormat('en-GB', opts).format(now);
        });
      }
      tick();
      setInterval(tick, 1000);
    }

    return { init: init };
  })();

  /* ── Loot.strip — [data-strip] editorial filmstrips.
     Two regimes by pointer (S119e). COARSE (phones): fully NATIVE —
     the track keeps CSS scroll-snap x mandatory + snap-stop always
     (the one-slide Instagram lock, done by the browser), scrolling
     stays on the compositor thread (smooth, pre-rasterized, no white
     flashes), and JS only drives the readout/thumb and the steppers
     (native smooth scrollTo). FINE (desktop): a continuous physics
     loop (the portfolio-reel model) owns wheel input — horizontal
     deltas feed a target, a lerp renders scrollLeft, a magnetic pull
     toward the nearest stop ramps in as input quiets; drag-to-pan +
     threshold settle. No-JS keeps the plain snap track. ── */
  Loot.strip = (function () {
    var SMOOTH = 0.13; // lerp toward target per frame
    var SNAP_STRENGTH = 0.09; // peak magnetic pull toward a stop
    var SNAP_ENGAGE = 260; // ms for the pull to ramp in after input stops
    var MAX_WHEEL = 240; // clamp one wheel event's contribution

    function setup(root) {
      // idempotent: streamed-in strips call init() again — set-up roots skip
      if (root.classList.contains('is-ready')) return;
      var track = root.querySelector('[data-strip-track]');
      var thumb = root.querySelector('[data-strip-thumb]');
      var pos = root.querySelector('[data-strip-pos]');
      var prev = root.querySelector('[data-strip-prev]');
      var next = root.querySelector('[data-strip-next]');
      if (!track || !thumb) return;

      var items = Array.prototype.slice.call(track.children);
      var total = items.length;
      var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      var raf = null;

      function pad(n) {
        return ('0' + n).slice(-2);
      }

      /* ── cached geometry (S119d perf pass) ──
         Layout is read ONLY here. The per-frame offsetLeft/scrollWidth
         reads interleaved with scrollLeft writes forced sync layout
         every frame and made touch paging jitter on phones. Refresh on
         resize and when a lazy image lands — never mid-gesture. */
      var G = { lefts: [], rights: [], stops: [], max: 0, sw: 1, cw: 1 };

      function refreshGeom() {
        G.sw = track.scrollWidth || 1;
        G.cw = track.clientWidth || 1;
        G.max = Math.max(0, G.sw - G.cw);
        G.lefts.length = G.rights.length = G.stops.length = 0;
        for (var i = 0; i < total; i++) {
          G.lefts.push(items[i].offsetLeft);
          G.rights.push(items[i].offsetLeft + items[i].offsetWidth);
          G.stops.push(Math.min(items[i].offsetLeft, G.max));
        }
      }

      function maxScroll() {
        return G.max;
      }

      function update() {
        raf = null;
        var max = G.max;
        var x = track.scrollLeft;
        // thumb: width = visible fraction, offset = scrolled fraction
        thumb.style.width = ((G.cw / G.sw) * 100).toFixed(2) + '%';
        thumb.style.transform =
          'translateX(' + (max > 0 ? (x / G.sw) * G.cw : 0) + 'px)';
        // readout: first visible item, 1-based
        if (pos) {
          var current = 0;
          for (var i = 0; i < total; i++) {
            if (G.rights[i] - x > 1) {
              current = i;
              break;
            }
          }
          if (max > 0 && x >= max - 1) current = total - 1;
          pos.textContent = pad(current + 1) + ' / ' + pad(total);
        }
        if (prev) prev.disabled = x <= 1;
        if (next) next.disabled = max <= 0 || x >= max - 1;
      }

      function schedule() {
        if (!raf) raf = requestAnimationFrame(update);
      }

      function clampX(x) {
        return Math.max(0, Math.min(x, G.max));
      }

      function stops() {
        return G.stops;
      }

      function nearestStop(x) {
        var all = G.stops;
        var best = 0;
        var bestD = Infinity;
        for (var i = 0; i < all.length; i++) {
          var d = Math.abs(all[i] - x);
          if (d < bestD) {
            bestD = d;
            best = all[i];
          }
        }
        if (Math.abs(G.max - x) < bestD) best = G.max;
        return best;
      }

      /* ── coarse pointers: NATIVE mode (S119e rework) ──
         Touch scrolling stays on the compositor thread — smooth,
         pre-rasterized (no white flashes) — and the strip CSS keeps
         scroll-snap x mandatory + snap-stop always there, which IS the
         one-slide Instagram lock, done by the browser. JS only drives
         the instruments and the steppers; the physics/settle system
         below never runs on touch. */
      var coarse = window.matchMedia('(pointer: coarse)').matches;
      if (coarse) {
        var stepTo = function (x) {
          if (reduced) {
            track.scrollLeft = clampX(x);
            return;
          }
          track.scrollTo({ left: clampX(x), behavior: 'smooth' });
        };
        if (next) {
          next.addEventListener('click', function () {
            var edge = track.scrollLeft + G.cw;
            for (var i = 0; i < total; i++) {
              if (G.rights[i] > edge + 2) return stepTo(G.lefts[i]);
            }
            stepTo(G.max);
          });
        }
        if (prev) {
          prev.addEventListener('click', function () {
            var goal = Math.max(0, track.scrollLeft - G.cw * 0.85);
            var to = 0;
            for (var i = total - 1; i >= 0; i--) {
              if (G.lefts[i] <= goal + 2) {
                to = G.lefts[i];
                break;
              }
            }
            stepTo(to);
          });
        }
        track.addEventListener('scroll', schedule, { passive: true });
        window.addEventListener('resize', function () {
          refreshGeom();
          schedule();
        });
        track.querySelectorAll('img').forEach(function (img) {
          img.draggable = false;
          if (!img.complete) {
            img.addEventListener('load', function () {
              refreshGeom();
              schedule();
            }, { once: true });
          }
        });
        root.classList.add('is-ready');
        refreshGeom();
        update();
        return;
      }

      /* ── the motion system (fine pointers) ── */
      var posF = track.scrollLeft; // rendered position (float)
      var target = posF; // where input wants to be
      var anchor = 0; // the stop the strip currently holds
      var lastInput = 0;
      var loopRaf = null;
      var deliberate = false; // goTo travel — target IS a stop, lock stands down

      function pitch() {
        return total > 0 ? G.rights[0] - G.lefts[0] || G.cw : G.cw;
      }

      function loop() {
        target = clampX(target);
        var engage = Math.min(1, (performance.now() - lastInput) / SNAP_ENGAGE);
        var calm = 1 - Math.min(1, Math.abs(target - posF) / (pitch() * 0.6));
        var snapPt = deliberate ? target : nearestStop(posF);
        // the lock: ramps in as input quiets, fades while travelling fast —
        // it takes hold as you settle, never fighting an active scroll.
        // Deliberate travel (steppers/settle) already aims at a stop.
        if (!deliberate) target += (snapPt - target) * SNAP_STRENGTH * engage * calm;
        posF += (target - posF) * SMOOTH;
        track.scrollLeft = posF;
        if (Math.abs(target - posF) < 0.3 && Math.abs(snapPt - posF) < 0.5 && engage >= 1) {
          posF = target = snapPt;
          track.scrollLeft = snapPt;
          anchor = snapPt;
          deliberate = false;
          loopRaf = null;
          return;
        }
        loopRaf = requestAnimationFrame(loop);
      }

      function startLoop() {
        if (!loopRaf) loopRaf = requestAnimationFrame(loop);
      }

      function stopLoop() {
        if (loopRaf) {
          cancelAnimationFrame(loopRaf);
          loopRaf = null;
        }
      }

      function syncFromNative() {
        posF = target = track.scrollLeft;
      }

      function goTo(x) {
        if (reduced) {
          var t = clampX(x);
          track.scrollLeft = t;
          posF = target = anchor = t;
          schedule();
          return;
        }
        if (!loopRaf) syncFromNative();
        target = clampX(x);
        deliberate = true;
        lastInput = performance.now();
        startLoop();
      }

      // desktop wheel: horizontal intent is ours — one continuous motion
      track.addEventListener(
        'wheel',
        function (e) {
          if (reduced) return;
          if (Math.abs(e.deltaX) <= Math.abs(e.deltaY)) return; // vertical → page
          e.preventDefault();
          e.stopPropagation();
          if (!loopRaf) syncFromNative();
          deliberate = false;
          lastInput = performance.now();
          target = clampX(target + Math.max(-MAX_WHEEL, Math.min(MAX_WHEEL, e.deltaX)));
          startLoop();
        },
        { passive: false }
      );

      // steppers land on item boundaries — deliberate travel
      function stepNext() {
        var x = loopRaf ? target : track.scrollLeft;
        var edge = x + track.clientWidth;
        for (var i = 0; i < total; i++) {
          if (items[i].offsetLeft + items[i].offsetWidth > edge + 2) {
            goTo(items[i].offsetLeft);
            return;
          }
        }
        goTo(maxScroll());
      }

      function stepPrev() {
        var x = loopRaf ? target : track.scrollLeft;
        var goal = Math.max(0, x - track.clientWidth * 0.85);
        var to = 0;
        for (var i = total - 1; i >= 0; i--) {
          if (items[i].offsetLeft <= goal + 2) {
            to = items[i].offsetLeft;
            break;
          }
        }
        goTo(to);
      }

      if (prev) prev.addEventListener('click', stepPrev);
      if (next) next.addEventListener('click', stepNext);
      track.addEventListener('scroll', schedule, { passive: true });

      // real resizes (orientation, chrome) relocate every stop — refresh
      // the geometry cache and re-anchor instantly rather than letting
      // the settle glide chase moved targets
      window.addEventListener('resize', function () {
        refreshGeom();
        schedule();
        if (loopRaf || dragging) return;
        var s = nearestStop(track.scrollLeft);
        if (Math.abs(s - track.scrollLeft) > 1) track.scrollLeft = s;
        posF = target = anchor = s;
      });

      // gesture over on the native path (touch momentum, keyboard): commit
      // to the next picture past 35% of the way, otherwise spring back
      function settle() {
        var x = track.scrollLeft;
        var delta = x - anchor;
        if (Math.abs(delta) <= 4) {
          anchor = nearestStop(x);
          return;
        }
        // micro-displacements (diagonal fingers during vertical page
        // scrolling) restore silently — a visible glide-back reads weird
        if (Math.abs(delta) <= 28) {
          track.scrollLeft = anchor;
          posF = target = anchor;
          return;
        }
        var near = nearestStop(x);
        if (near !== anchor) {
          goTo(near);
          return;
        }
        var all = stops();
        var dir = delta > 0 ? 1 : -1;
        var neighbor = null;
        for (var i = 0; i < all.length; i++) {
          if (dir === 1 && all[i] > anchor + 2) { neighbor = all[i]; break; }
          if (dir === -1 && all[i] < anchor - 2) neighbor = all[i];
        }
        if (dir === 1 && neighbor === null) neighbor = maxScroll();
        if (neighbor !== null && Math.abs(delta) >= Math.abs(neighbor - anchor) * 0.35) {
          goTo(neighbor);
        } else {
          goTo(anchor);
        }
      }

      // one scrollLeft write per frame, whatever rate the pointer fires
      // at (S119d: off-rAF writes were the jitter)
      var dragging = false; // mouse drag
      var dragRaf = null;
      var dragX = 0;
      function applyDrag() {
        dragRaf = null;
        track.scrollLeft = dragX;
        posF = target = dragX;
      }
      function queueDrag(x) {
        dragX = clampX(x);
        if (!dragRaf) dragRaf = requestAnimationFrame(applyDrag);
      }
      function flushDrag() {
        if (dragRaf) {
          cancelAnimationFrame(dragRaf);
          applyDrag();
        }
      }

      var settleTimer = null;
      function scheduleSettle() {
        if (dragging) return; // our own writes — no settle churn
        if (settleTimer) clearTimeout(settleTimer);
        settleTimer = setTimeout(function () {
          if (!dragging && !loopRaf) settle();
        }, 140);
      }

      track.addEventListener('scroll', scheduleSettle, { passive: true });
      track.addEventListener(
        'touchstart',
        function () {
          stopLoop(); // native momentum takes the wheel back
          scheduleSettle();
        },
        { passive: true }
      );

      // drag-to-pan (mouse only — coarse pointers never reach this code)
      var startX = 0;
      var startLeft = 0;
      track.addEventListener('pointerdown', function (e) {
        if (e.pointerType !== 'mouse' || e.button !== 0) return;
        stopLoop();
        dragging = true;
        startX = e.clientX;
        startLeft = track.scrollLeft;
        track.classList.add('is-dragging');
        try { track.setPointerCapture(e.pointerId); } catch (err) {}
      });
      track.addEventListener('pointermove', function (e) {
        if (!dragging) return;
        lastInput = performance.now();
        queueDrag(startLeft - (e.clientX - startX));
      });
      function endDrag() {
        if (!dragging) return;
        dragging = false;
        flushDrag();
        track.classList.remove('is-dragging');
        settle();
      }
      track.addEventListener('pointerup', endDrag);
      track.addEventListener('pointercancel', endDrag);

      track.querySelectorAll('img').forEach(function (img) {
        img.draggable = false;
      });

      root.classList.add('is-ready');
      refreshGeom();
      update();
      // late image loads change scrollWidth — refresh cache + instruments
      track.querySelectorAll('img').forEach(function (img) {
        if (!img.complete) {
          img.addEventListener('load', function () {
            refreshGeom();
            schedule();
          }, { once: true });
        }
      });
    }

    function init() {
      document.querySelectorAll('[data-strip]').forEach(setup);
    }

    return { init: init };
  })();

  /* ── Loot.scan — touch counterpart of the cursor thumbnail. Coarse
     pointers can't hover, so scrolling IS the cursor: the [data-preview]
     row crossing the focus line (45% of the viewport) becomes the live
     one — it takes the hover highlight and its garment shows in a small
     frame docked bottom-right. Data display, not decoration — stays on
     under reduced motion. Rows opt in via a [data-scan] list container. ── */
  Loot.scan = (function () {
    function init() {
      if (window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
      if (!document.querySelector('[data-scan] [data-preview]')) return;

      var frame = document.createElement('div');
      frame.className = 'scan-frame';
      frame.hidden = true;
      var img = document.createElement('img');
      img.alt = '';
      img.width = 360;
      img.height = 450;
      frame.appendChild(img);
      document.body.appendChild(frame);

      var active = null;
      var raf = null;

      function clearActive() {
        if (active) active.classList.remove('is-scanned');
        active = null;
        frame.hidden = true;
      }

      function scan() {
        raf = null;
        // an open overlay (filter panel, menu) suspends the scan read
        if (document.querySelector('.catalog__panel.is-open, .menu-overlay.is-open')) {
          clearActive();
          return;
        }
        var el = document.elementFromPoint(
          Math.round(window.innerWidth * 0.5),
          Math.round(window.innerHeight * 0.45)
        );
        var row = el && el.closest ? el.closest('[data-preview]') : null;
        if (row && !row.closest('[data-scan]')) row = null;
        if (row === active) return;
        if (active) active.classList.remove('is-scanned');
        active = row;
        if (!row) {
          frame.hidden = true;
          return;
        }
        row.classList.add('is-scanned');
        frame.hidden = !Loot.preview.set(img, row);
      }

      function schedule() {
        if (!raf) raf = requestAnimationFrame(scan);
      }

      window.addEventListener('scroll', schedule, { passive: true });
      window.addEventListener('resize', schedule, { passive: true });
      // overlays open/close without scrolling — re-read on any click
      document.addEventListener('click', schedule, true);
      schedule();
    }

    return { init: init };
  })();

  /* ── Loot.dropgate — each element releases at its own epoch. ── */
  Loot.dropgate = (function () {
    var timer = null;

    function refresh() {
      var now = Date.now() / 1000;
      var pending = false;
      document.querySelectorAll('[data-drop-until]').forEach(function (el) {
        var until = Number(el.getAttribute('data-drop-until'));
        if (!Number.isFinite(until) || until <= 0) return;
        if (until > now) {
          pending = true;
          return;
        }
        if (el.hasAttribute('data-drop-chip')) {
          el.remove();
          return;
        }
        if (el.hasAttribute('data-drop-locked-ui')) el.hidden = true;
        if (el.hasAttribute('data-drop-ready-ui')) el.hidden = false;
        if (el.hasAttribute('data-drop-swap')) el.textContent = el.getAttribute('data-drop-swap');
        el.removeAttribute('data-drop-until');
      });
      if (pending && !timer) timer = setInterval(refresh, 1000);
      if (!pending && timer) {
        clearInterval(timer);
        timer = null;
      }
    }

    function init() {
      document.addEventListener('loot:drop-live', refresh);
      window.addEventListener('pageshow', refresh);
      refresh();
    }
    return { init: init, refresh: refresh };
  })();

  /* ── Loot.preview — instant hover thumbnails for [data-preview] rows.
     Rows carry data-preview (sharp) + data-preview-lq (tiny). After the
     page settles, every lq then every sharp URL prefetches in small idle
     batches, so the first hover paints from cache. set() shows whatever
     is ready — sharp if cached, else lq immediately with an in-place
     upgrade the moment the sharp file lands. ── */
  Loot.preview = (function () {
    var hiReady = {};
    var queued = {};
    var fetched = {};
    var lqQ = [];
    var hiQ = [];
    var inflight = 0;
    var MAX_INFLIGHT = 4;
    var started = false;
    var bumpIO = null;

    /* one global pump: EVERY tiny thumb on the page (streamed-in pages
       included — they enqueue at the front of the line) downloads before
       ANY sharp one, capped so live hover requests always have bandwidth */
    function pump() {
      while (inflight < MAX_INFLIGHT) {
        var url = lqQ.shift();
        var isHi = false;
        if (!url) {
          url = hiQ.shift();
          isHi = true;
        }
        if (!url) return;
        inflight++;
        (function (u, hi) {
          var im = new Image();
          im.onload = im.onerror = function () {
            fetched[u] = true;
            if (hi && im.naturalWidth > 0) hiReady[u] = true;
            inflight--;
            setTimeout(pump, hi ? 60 : 0);
          };
          im.src = u;
        })(url, isHi);
      }
    }

    /* the reader's viewport owns the queue: a row scrolling into view
       bumps its thumbs to the front, so deep rows on a fresh load are
       warm by the time the cursor reaches them */
    function bump(row) {
      var l = row.getAttribute('data-preview-lq');
      var h = row.getAttribute('data-preview');
      if (l && !fetched[l]) {
        lqQ = lqQ.filter(function (x) { return x !== l; });
        lqQ.unshift(l);
      }
      if (h && !fetched[h]) {
        hiQ = hiQ.filter(function (x) { return x !== h; });
        hiQ.unshift(h);
      }
      if (started) pump();
    }

    function set(img, row) {
      var hi = row.getAttribute('data-preview');
      if (!hi) return false;
      var lq = row.getAttribute('data-preview-lq');
      img.setAttribute('fetchpriority', 'high');
      img._want = hi;
      if (hiReady[hi]) {
        if (img.getAttribute('src') !== hi) img.setAttribute('src', hi);
        return true;
      }
      if (lq && img.getAttribute('src') !== lq) img.setAttribute('src', lq);
      var probe = new Image();
      probe.onload = function () {
        hiReady[hi] = true;
        if (img._want === hi) img.setAttribute('src', hi);
      };
      probe.src = hi;
      return true;
    }

    function enqueue(scope) {
      var rows = (scope || document).querySelectorAll('[data-preview]:not([data-preview-warmed])');
      if (!rows.length) return;
      if (!bumpIO && 'IntersectionObserver' in window) {
        bumpIO = new IntersectionObserver(function (entries) {
          entries.forEach(function (entry) {
            if (!entry.isIntersecting) return;
            bumpIO.unobserve(entry.target);
            bump(entry.target);
          });
        }, { rootMargin: '300px 0px 300px 0px' });
      }
      rows.forEach(function (r) {
        r.setAttribute('data-preview-warmed', '');
        var l = r.getAttribute('data-preview-lq');
        var h = r.getAttribute('data-preview');
        if (l && !queued[l]) { queued[l] = true; lqQ.push(l); }
        if (h && !queued[h]) { queued[h] = true; hiQ.push(h); }
        if (bumpIO) bumpIO.observe(r);
      });
      if (started) pump();
    }

    function warm(scope) {
      enqueue(scope);
      if (!started) {
        var kick = function () { started = true; pump(); };
        if ('requestIdleCallback' in window) requestIdleCallback(kick, { timeout: 2000 });
        else setTimeout(kick, 1200);
      }
    }

    function init() {
      if (!document.querySelector('[data-preview]')) return;
      if (document.readyState === 'complete') warm(document);
      else window.addEventListener('load', function () { warm(document); }, { once: true });
    }

    return { init: init, set: set, warm: warm };
  })();

  /* ── Loot.lightbox — full-image viewer for [data-lightbox] galleries.
     Click any .story__media img inside a [data-lightbox] root to open it
     full-screen (2048w request); prev/next steppers, arrow keys, wheel
     and touch swipe walk the root's gallery. The attribute value labels
     the bar. Drag-to-pan on the strips never opens it (8px guard).
     Speed: opening paints the page's already-loaded file instantly and
     upgrades to 2048w in place; every shown image warms both neighbors,
     so stepping swaps from cache with a short fade. ── */
  Loot.lightbox = (function () {
    var overlay, imgEl, labelEl, posEl, lastFocus;
    var gallery = [];
    var idx = 0;
    var downX = 0;
    var downY = 0;
    var touchX = null;
    var showToken = 0;
    var closeTimer = null;
    var warmed = {};
    var wheelAcc = 0;
    var wheelLock = 0;

    function pad(n) { return ('0' + n).slice(-2); }

    function reduced() {
      return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }

    function build() {
      if (overlay) return;
      overlay = document.createElement('div');
      overlay.className = 'lightbox';
      overlay.setAttribute('role', 'dialog');
      overlay.setAttribute('aria-modal', 'true');
      overlay.hidden = true;
      overlay.innerHTML =
        '<div class="lightbox__bar">' +
          '<span class="lightbox__label" data-lightbox-label></span>' +
          '<span class="lightbox__ctl">' +
            '<span class="lightbox__pos" data-lightbox-pos aria-hidden="true"></span>' +
            '<button class="lightbox__btn" type="button" data-lightbox-prev>&larr;</button>' +
            '<button class="lightbox__btn" type="button" data-lightbox-next>&rarr;</button>' +
            '<button class="lightbox__btn lightbox__btn--close" type="button" data-lightbox-close>&times;</button>' +
          '</span>' +
        '</div>' +
        '<div class="lightbox__stage" data-lightbox-stage><img alt="" data-lightbox-img></div>';
      document.body.appendChild(overlay);
      imgEl = overlay.querySelector('[data-lightbox-img]');
      labelEl = overlay.querySelector('[data-lightbox-label]');
      posEl = overlay.querySelector('[data-lightbox-pos]');
      var i18n = document.documentElement.getAttribute('data-lightbox-i18n');
      var labels = { close: 'Close', prev: 'Previous image', next: 'Next image' };
      try { if (i18n) labels = JSON.parse(i18n); } catch (e) { /* defaults hold */ }
      overlay.querySelector('[data-lightbox-prev]').setAttribute('aria-label', labels.prev);
      overlay.querySelector('[data-lightbox-next]').setAttribute('aria-label', labels.next);
      overlay.querySelector('[data-lightbox-close]').setAttribute('aria-label', labels.close);

      overlay.querySelector('[data-lightbox-prev]').addEventListener('click', function () { show(idx - 1); });
      overlay.querySelector('[data-lightbox-next]').addEventListener('click', function () { show(idx + 1); });
      overlay.querySelector('[data-lightbox-close]').addEventListener('click', close);
      // the stage backdrop closes; the image itself advances
      overlay.querySelector('[data-lightbox-stage]').addEventListener('click', function (e) {
        if (e.target === imgEl) show(idx + 1);
        else close();
      });
      overlay.addEventListener('touchstart', function (e) { touchX = e.changedTouches[0].clientX; }, { passive: true });
      overlay.addEventListener('touchend', function (e) {
        if (touchX === null) return;
        var dx = e.changedTouches[0].clientX - touchX;
        touchX = null;
        if (dx > 44) show(idx - 1);
        else if (dx < -44) show(idx + 1);
      }, { passive: true });
      // wheel/trackpad flips through the gallery — accumulate, then lock
      // briefly so trackpad momentum doesn't machine-gun past images
      overlay.addEventListener('wheel', function (e) {
        e.preventDefault();
        var now = Date.now();
        if (now < wheelLock) return;
        wheelAcc += e.deltaY;
        if (Math.abs(wheelAcc) > 60) {
          show(idx + (wheelAcc > 0 ? 1 : -1));
          wheelAcc = 0;
          wheelLock = now + 320;
        }
      }, { passive: false });
    }

    function fullSrc(img) {
      var s = img.currentSrc || img.src;
      return s.replace(/width=\d+/, 'width=2048');
    }

    function warm(i) {
      var g = gallery[(i + gallery.length) % gallery.length];
      if (!g) return;
      var u = fullSrc(g);
      if (warmed[u]) return;
      warmed[u] = true;
      var im = new Image();
      im.decoding = 'async';
      im.src = u;
    }

    /* paint src into the stage; anim = run the short settle fade */
    function swap(src, alt, anim) {
      overlay.classList.remove('is-waiting');
      imgEl.alt = alt || '';
      imgEl.classList.remove('lightbox__img-anim');
      if (anim && !reduced()) {
        void imgEl.offsetWidth; /* restart the animation */
        imgEl.src = src;
        imgEl.classList.add('lightbox__img-anim');
      } else {
        imgEl.src = src;
      }
    }

    function show(i) {
      idx = (i + gallery.length) % gallery.length;
      var g = gallery[idx];
      posEl.textContent = pad(idx + 1) + ' / ' + pad(gallery.length);
      var src = fullSrc(g);
      var token = ++showToken;
      var done = false;
      var soft = false;
      var finish = function () {
        if (done || token !== showToken) return;
        done = true;
        swap(src, g.alt, !soft);
        warm(idx + 1);
        warm(idx - 1);
      };
      var pre = new Image();
      pre.decoding = 'async';
      pre.onload = finish;
      pre.onerror = finish;
      pre.src = src;
      if (pre.complete && pre.naturalWidth > 0) {
        finish(); /* cached — swap now */
      } else {
        /* not cached: paint the file the page already has THIS frame,
           then let the 2048w land silently in place */
        var lq = g.currentSrc || g.src;
        if (lq && lq !== src) {
          swap(lq, g.alt, true);
          soft = true;
        }
        overlay.classList.add('is-waiting');
      }
    }

    function open(root, img) {
      build();
      if (closeTimer) { clearTimeout(closeTimer); closeTimer = null; }
      gallery = Array.prototype.slice.call(root.querySelectorAll('.story__media img'));
      if (!gallery.length) return;
      labelEl.textContent = root.getAttribute('data-lightbox') || '';
      lastFocus = document.activeElement;
      wheelAcc = 0;
      overlay.hidden = false;
      void overlay.offsetWidth; /* commit hidden=false before the fade starts */
      overlay.classList.add('is-open');
      document.documentElement.classList.add('lightbox-open');
      var lenis = Loot.scroll && Loot.scroll.instance;
      if (lenis && lenis.stop) lenis.stop();
      show(gallery.indexOf(img));
      overlay.querySelector('[data-lightbox-close]').focus();
    }

    function close() {
      showToken++; /* cancel any in-flight swap */
      overlay.classList.remove('is-open');
      document.documentElement.classList.remove('lightbox-open');
      var fin = function () {
        closeTimer = null;
        overlay.hidden = true;
        imgEl.src = '';
        imgEl.classList.remove('lightbox__img-anim');
        overlay.classList.remove('is-waiting');
      };
      if (reduced()) fin();
      else closeTimer = setTimeout(fin, 220);
      var lenis = Loot.scroll && Loot.scroll.instance;
      if (lenis && lenis.start) lenis.start();
      if (lastFocus && lastFocus.focus) lastFocus.focus();
    }

    function init() {
      document.addEventListener('pointerdown', function (e) {
        downX = e.clientX;
        downY = e.clientY;
      }, true);
      var SEL = '[data-lightbox] .story__media img, [data-media-link] .story__media img';
      document.addEventListener('click', function (e) {
        var img = e.target.closest ? e.target.closest(SEL) : null;
        if (!img) {
          // the strips' pointer capture retargets clicks to the track —
          // recover the actual image from the click coordinates
          var at = document.elementFromPoint(e.clientX, e.clientY);
          img = at && at.closest ? at.closest(SEL) : null;
        }
        if (!img) return;
        if (Math.abs(e.clientX - downX) > 8 || Math.abs(e.clientY - downY) > 8) return;
        // data-media-link roots navigate (wardrobe index rows) instead of previewing
        var link = img.closest('[data-media-link]');
        if (link) {
          window.location.href = link.getAttribute('data-media-link');
          return;
        }
        e.preventDefault();
        open(img.closest('[data-lightbox]'), img);
      });
      document.addEventListener('keydown', function (e) {
        if (!overlay || overlay.hidden) return;
        if (e.key === 'Escape') close();
        else if (e.key === 'ArrowLeft') show(idx - 1);
        else if (e.key === 'ArrowRight') show(idx + 1);
      });
    }

    return { init: init };
  })();

  /* ── boot ── */
  function boot() {
    Loot.loader.init();
    Loot.scroll.init();
    Loot.reveal.init();
    Loot.decode.init();
    Loot.newsletter.init();
    Loot.dropgate.init(); // before countdown — a past-dated countdown fires at boot
    Loot.countdown.init();
    Loot.clock.init();
    Loot.strip.init();
    Loot.scan.init();
    Loot.lightbox.init();
    Loot.preview.init();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
